import { lazy } from "react";
import { Navigate, RouteObject, useRoutes } from "react-router";
import { getCurrentUserDetailsFromLocalStorage } from "../api/authApi.ts";
import {
  CreditBasedAccess,
  RequireAiTalentDiscovery,
} from "./AccessBasedRoutes.tsx";
import { RoutePaths } from "./constants.ts";

const CreateRole = lazy(
  () => import("../features/Interview/Designer/page/CreateRole.tsx")
);
const MatchAssessment = lazy(
  () => import("../features/Interview/MatchAssessment/page/MatchAssessment.tsx")
);
const UsagePage = lazy(
  () => import("../features/Billings/Wallet/page/UsagePage.tsx")
);
const BillingPage = lazy(
  () => import("../features/Billings/Wallet/page/BillingPage.tsx")
);
const RedirectToWizard = lazy(() => import("./RedirectToWizard.tsx"));
const RoleWrapper = lazy(
  () => import("../features/Role/pages/RoleWrapper.tsx")
);
const InterviewSettings = lazy(
  () =>
    import("../features/Interview/InterviewSettings/page/InterviewSettings.tsx")
);
const IntegrateCalendlyDialog = lazy(
  () => import("../features/Role/IntegrateCalendly/pages/IntegrateCalendly.tsx")
);
const InviteCandidates = lazy(
  () => import("../features/Role/InviteCandidates/page/InviteCandidates.tsx")
);
const ApplicantsListPage = lazy(
  () =>
    import("../features/Applicants/ApplicantsList/pages/ApplicantsListPage.tsx")
);
const ApplicantHubPage = lazy(
  () => import("../features/ApplicantHub/pages/ApplicantHubPage.tsx")
);
const AuthRoutes = lazy(() => import("./AuthRoutes.tsx"));

const CandidateListSwitch = lazy(
  () =>
    import("../features/OldVersionCandidate/RoleCandidates/CandidateListSwitch.tsx")
);
const Home = lazy(() => import("../features/Home/Home.tsx"));
const AuthPage = lazy(() => import("../features/AuthPage/index.tsx"));
const CandidateReport = lazy(() => import("../pages/CandidateReport.tsx"));
const CandidateProfile = lazy(() => import("../pages/CandidateProfile.tsx"));
const PublicRoleApply = lazy(
  () => import("../features/Applicants/ApplyRole/pages/PublicRoleApply.tsx")
);
const PhoneCandidateReport = lazy(
  () => import("../pages/PhoneCandidateReport.tsx")
);
const RolesList = lazy(
  () => import("../features/Role/RolesList/pages/RolesListPage.tsx")
);
const UserManagement = lazy(
  () => import("../features/Settings/UserManagement.tsx")
);

const ATSIntegrations = lazy(
  () => import("../features/Settings/ATSIntegrations.tsx")
);
const CompanyDetails = lazy(
  () => import("../features/Settings/CompanyDetails/CompanyDetails.tsx")
);
const CalendlyDialog = lazy(
  () => import("../pages/jobWidgets/CalendlyDialog.tsx")
);
const EditJobSwitcher = lazy(
  () => import("../pages/jobWidgets/EditJobSwitcher.tsx")
);
const InviteCandidatesSwitcher = lazy(
  () => import("../pages/jobWidgets/InviteCandidatesSwitcher.tsx")
);

const ViewCandidates = lazy(
  () =>
    import("../features/OldVersionCandidate/RoleCandidates/ViewCandidates.tsx")
);

const AnalyticsPage = lazy(
  () => import("../features/Analytics/pages/AnalyticsPage.tsx")
);

const ActivityLog = lazy(
  () => import("../features/ActivityLog/pages/ActivityLog.tsx")
);

const NotFoundPage = lazy(() => import("../app/pages/ErrorPage/404.tsx"));
const CandidateDetail = lazy(() => import("../pages/CandidateDetail.tsx"));
const CompanyInfoForm = lazy(() => import("../pages/CompanyInfoForm.tsx"));

const ApplicantProfilePage = lazy(
  () =>
    import("../features/Applicants/ApplicantPage/pages/ApplicantProfilePage.tsx")
);
const InterviewDesigner = lazy(
  () => import("../features/Interview/Designer/page/InterviewDesigner.tsx")
);

const SourcingShell = lazy(
  () => import("../features/Sourcing/pages/SourcingShell.tsx")
);

const SavedProfilesPage = lazy(
  () => import("../features/Sourcing/pages/SavedProfilesPage.tsx")
);

// Unauthenticated routes
const unauthenticatedRoutes = [
  // Present in both route tables: right after OTP the auth data is written
  // and the app heads here — this route must never fall into the login
  // catch-all, whichever table a given render still holds.
  { path: "onboard-form", element: <CompanyInfoForm /> },
  {
    path: "/:slug/pdf/:candidateId/",
    element: <CandidateReport />,
  },
  {
    path: "/:slug/:interviewType/pdf/:candidateId/",
    element: <PhoneCandidateReport />,
  },
  {
    path: "/:slug/:interviewType/candidate/:candidateId",
    element: <CandidateProfile />,
  },
  {
    path: "/:slug/roles/:roleId/apply",
    element: <PublicRoleApply />,
  },
  {
    path: "*",
    element: <AuthPage />,
    children: [
      {
        index: true,
        path: "?*",
        element: <AuthRoutes />,
      },
    ],
  },
];

// Authenticated routes
const authenticatedRoutes: RouteObject[] = [
  { path: "onboard-form", element: <CompanyInfoForm /> },
  {
    path: "/:slug/",
    element: <CreditBasedAccess />,
    children: [
      {
        index: true,
        element: <Home />,
      },
     
      {
        path: "analytics",
        element: <AnalyticsPage />,
      },
      {
        path: "applicant-hub",
        element: <ApplicantHubPage />,
      },
      {
        path: "candidate-search",
        element: (
          <RequireAiTalentDiscovery>
            <SourcingShell />
          </RequireAiTalentDiscovery>
        ),
      },
      {
        path: "saved-profiles",
        element: (
          <RequireAiTalentDiscovery>
            <SavedProfilesPage />
          </RequireAiTalentDiscovery>
        ),
      },
      {
        path: "activity-log",
        element: <ActivityLog />,
      },
      {
        path: "roles",
        children: [
          {
            index: true,
            element: <RolesList />,
          },
          {
            path: "create", // Create New Role
            children: [
              {
                index: true,
                element: <CreateRole />,
              },

              {
                path: ":roleId", // Continue assessment creation
                children: [
                  {
                    index: true,
                    element: <InterviewDesigner />,
                  },
                  {
                    path: ":jdId",
                    element: <InterviewDesigner />,
                  },
                ],
              },
            ],
          },
          {
            path: ":roleId/",
            element: <RoleWrapper />,
            children: [
              {
                index: true,
                element: <Navigate to={RoutePaths.APPLICANTS} />,
              },
              // Role based routes
              {
                path: "match",
                element: <MatchAssessment />,
              },
              {
                path: RoutePaths.INVITE_CANDIDATES,
                element: <InviteCandidates />,
              },
              {
                path: RoutePaths.CALENDLY,
                element: <IntegrateCalendlyDialog />,
              },
              {
                path: RoutePaths.APPLICANTS,
                element: <ApplicantsListPage />,
              },
              {
                path: "applicants/:applicantId",
                element: <ApplicantProfilePage />,
              },
              {
                path: RoutePaths.SETTINGS,
                element: <RedirectToWizard step="Settings" />,
              },
              {
                path: ":interviewType",
                children: [
                  {
                    path: RoutePaths.DESIGNER_PREVIEW,
                    element: <RedirectToWizard step="Designer" />,
                  },
                  {
                    path: RoutePaths.SCORE_CONFIG,
                    element: <RedirectToWizard step="Criteria" />,
                  },
                  {
                    path: RoutePaths.SETTINGS,
                    element: <InterviewSettings />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "roles/:roleId/apply",
        element: <PublicRoleApply />,
      },
      {
        path: "settings",
        children: [
          {
            index: true,
            element: <Navigate to={"company-details"} />,
          },
          {
            path: "wallet",
            element: <Navigate to="wallet/usage" replace />,
          },
          {
            // Email templates moved into each role's settings.
            path: "mail-templates",
            element: <Navigate to="../company-details" replace />,
          },
          {
            path: "wallet/usage",
            element: <UsagePage />,
          },
          {
            path: "wallet/billing",
            element: <BillingPage />,
          },
          {
            path: "user-management",
            index: true,
            element: <UserManagement />,
          },
          {
            path: "ats-integrations",
            element: <ATSIntegrations />,
          },
          {
            path: "company-details",
            element: <CompanyDetails />,
          },
        ],
      },
      {
        path: ":interviewType/candidate/:candidateId",
        element: <CandidateProfile />,
      },

      {
        path: "pdf/:candidateId/",
        element: <CandidateReport />,
      },
      {
        path: ":interviewType/pdf/:candidateId/",
        element: <PhoneCandidateReport />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];

const AppRoutes = () => {

  const user = getCurrentUserDetailsFromLocalStorage();

  const routes: RouteObject[] = !!user
    ? authenticatedRoutes
    : unauthenticatedRoutes;

  return useRoutes(routes);
};

export default AppRoutes;
